import {
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
  SxProps,
  useMediaQuery,
  Theme,
} from '@mui/material';
import React from 'react';
import { AppTheme } from '@/theme';
import { mergeSx } from '@/shared/utils/merge-sx.util';

// Extend the MUI Tooltip slotProps to allow data-testid
interface ExtendedTooltipSlotProps {
  tooltip?: {
    'data-testid'?: string;
    sx?: SxProps<Theme>;
  };
}

export interface TooltipProps extends MuiTooltipProps {
  children: React.ReactElement;
  title: string | React.ReactElement | undefined;
  tooltipSx?: SxProps<Theme>;
  offset?: number;
  slotProps?: ExtendedTooltipSlotProps & MuiTooltipProps['slotProps'];
  placement?: MuiTooltipProps['placement'];
  mobilePlacement?: MuiTooltipProps['placement'];
}

export const Tooltip = ({
  children,
  title,
  tooltipSx,
  offset = 0,
  placement,
  mobilePlacement,
  ...props
}: TooltipProps) => {
  const isMobile = useMediaQuery('(max-width: 600px)');

  const getPlacement = () => {
    if (isMobile) return mobilePlacement || 'top';
    return placement || 'right-start';
  };

  if (!title) {
    return children;
  }

  return (
    <MuiTooltip
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, offset],
              },
            },
          ],
        },
        tooltip: {
          sx: mergeSx(
            { ...styles.tooltip, transform: isMobile ? 'translateY(-100%)' : 'none' },
            tooltipSx,
          ),
        },
        arrow: {
          sx: mergeSx(styles.arrow),
        },
      }}
      title={title}
      enterTouchDelay={0}
      leaveTouchDelay={0}
      enterDelay={0}
      leaveDelay={0}
      placement={getPlacement()}
      {...props}
    >
      {children}
    </MuiTooltip>
  );
};

const styles = {
  tooltip: {
    display: 'flex',
    padding: '4px 8px',
    m: '0 !important',
    minHeight: 'auto !important',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: (theme: AppTheme) => theme.palette.primary.main,
    color: (theme: AppTheme) => theme.palette.primary.light,
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '0px',
  },
  arrow: {
    display: 'none',
  },
};
